import { ReactElement, useMemo } from 'react'
import { FormControl, MenuItem, Select, Typography } from '@mui/material'
import type { IfcModelMetadata } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import {
  getGuidToStorey,
  upsertStoreyAssignment,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import type { MetadataUpdater } from '@editor/hooks/requests/useUpdateIfcModelMetadata'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useIsUpdatingIfcModelMetadata } from '@editorHooks'

/** Dropdown sentinel for clearing an element's storey assignment. */
export const CLEAR_STOREY_VALUE = '-'

interface Props {
  /** Elements the picked storey is assigned to. The control renders nothing when this is empty. */
  guids: string[]
  metadata: IfcModelMetadata | null
  storeyOptions: string[]
  disabled?: boolean
  updateIfcModelMetadata: (updater: MetadataUpdater) => void
  /** Called after a successful assignment (e.g. to clear the scene selection). */
  onAssigned?: () => void
}

/**
 * Storey dropdown that assigns the picked storey to `guids` on change (no
 * confirmation). Shows the selection's current storey ("-" when none), or a placeholder when it
 * spans multiple storeys. The "-" option clears the storey of the selected elements. Shared by the
 * IFC Import storey-assignment panel and the IFC element form's "Stockwerk zuweisen" tab.
 */
const IFCStoreyAssignmentSelect = ({
  guids,
  metadata,
  storeyOptions,
  disabled,
  updateIfcModelMetadata,
  onAssigned,
}: Props): ReactElement | null => {
  const isUpdating = useIsUpdatingIfcModelMetadata()

  const guidToStorey = useMemo(
    () => getGuidToStorey(metadata?.storeyAssignments),
    [metadata?.storeyAssignments],
  )

  // Storey shared by all selected elements ("-" when none of them has one); empty when the
  // selection spans multiple storeys.
  const selectedStorey = useMemo<string>(() => {
    if (guids.length === 0) return ''
    const storeys = new Set(guids.map(guid => guidToStorey[guid] ?? CLEAR_STOREY_VALUE))
    return storeys.size === 1 ? (Array.from(storeys)[0] as string) : ''
  }, [guids, guidToStorey])

  const handleChange = (storey: string) => {
    if (!storey || guids.length === 0) return
    const selectedSet = new Set(guids)
    updateIfcModelMetadata(current => {
      if (!current) return current

      // "-" clears any existing storey assignment of the selected elements.
      if (storey === CLEAR_STOREY_VALUE) {
        return {
          ...current,
          storeyAssignments: (current.storeyAssignments ?? [])
            .map(group => group.filter(assignment => !selectedSet.has(assignment.elementId)))
            .filter(group => group.length > 0),
        }
      }

      return {
        ...current,
        storeyAssignments: guids.reduce(
          (acc, elementId) => upsertStoreyAssignment(acc, { elementId, storey }),
          current.storeyAssignments ?? [],
        ),
      }
    })
    onAssigned?.()
  }

  // Placeholder shown when no single storey is selected: nothing selected vs. a mixed selection.
  const placeholderLabel = guids.length === 0 ? 'Stockwerk auswählen' : 'Verschiedene Stockwerke'

  return (
    <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }} fullWidth>
      <Select
        variant="outlined"
        value={selectedStorey}
        onChange={event => handleChange(event.target.value)}
        displayEmpty
        disabled={disabled || storeyOptions.length === 0 || guids.length === 0 || isUpdating}
        size="small"
        sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
        renderValue={value => {
          if (!value) {
            return (
              <Typography component="span" color="text.secondary">
                {placeholderLabel}
              </Typography>
            )
          }
          return value === CLEAR_STOREY_VALUE ? CLEAR_STOREY_VALUE : storeyToLabel(value)
        }}
      >
        <MenuItem value={CLEAR_STOREY_VALUE}>{CLEAR_STOREY_VALUE}</MenuItem>
        {storeyOptions.map(storey => (
          <MenuItem key={storey} value={storey}>
            {storeyToLabel(storey)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default IFCStoreyAssignmentSelect
